import React from 'react';
import { motion } from 'framer-motion';
interface AuthLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle: string;
}
export function AuthLayout({
  children,
  title,
  subtitle
}: AuthLayoutProps) {
  return <div className="min-h-screen bg-game-bg-pink dark:bg-[#1a1d29] flex" data-id="element-65">
      {/* Left Side - Image/Illustration (Hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-game-purple via-game-teal to-game-coral p-12 items-center justify-center relative overflow-hidden" data-id="element-66">
        {/* Animated Background Circles */}
        <motion.div animate={{
        scale: [1, 1.2, 1],
        rotate: [0, 180, 360]
      }} transition={{
        duration: 20,
        repeat: Infinity,
        ease: 'linear'
      }} className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" data-id="element-67" />
        <motion.div animate={{
        scale: [1.2, 1, 1.2],
        rotate: [360, 180, 0]
      }} transition={{
        duration: 15,
        repeat: Infinity,
        ease: 'linear'
      }} className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" data-id="element-68" />

        {/* Content */}
        <div className="relative z-10 max-w-md text-center" data-id="element-69">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-70">
            {/* Large Logo Illustration */}
            <div className="w-64 h-64 mx-auto mb-8" data-id="element-71">
              <svg viewBox="0 0 200 200" className="w-full h-full drop-shadow-2xl" data-id="element-72">
                <defs data-id="element-73">
                  <linearGradient id="authGradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-74">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" data-id="element-75" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0.6" data-id="element-76" />
                  </linearGradient>
                  <filter id="authGlow" data-id="element-77">
                    <feGaussianBlur stdDeviation="4" result="coloredBlur" data-id="element-78" />
                    <feMerge data-id="element-79">
                      <feMergeNode in="coloredBlur" data-id="element-80" />
                      <feMergeNode in="SourceGraphic" data-id="element-81" />
                    </feMerge>
                  </filter>
                </defs>

                {/* Zen circle */}
                <circle cx="100" cy="100" r="90" fill="none" stroke="url(#authGradient)" strokeWidth="4" opacity="0.4" data-id="element-82" />

                {/* Incomplete circle (Zen symbol) */}
                <motion.path d="M 100 10 A 90 90 0 1 1 99.9 10" fill="none" stroke="url(#authGradient)" strokeWidth="8" strokeLinecap="round" filter="url(#authGlow)" initial={{
                pathLength: 0
              }} animate={{
                pathLength: 1
              }} transition={{
                duration: 2,
                ease: 'easeInOut',
                repeat: Infinity,
                repeatDelay: 1
              }} data-id="element-83" />

                {/* Quest arrow/mountain */}
                <motion.path d="M 100 60 L 70 120 L 100 105 L 130 120 Z" fill="url(#authGradient)" opacity="0.9" initial={{
                scale: 0.8,
                opacity: 0.5
              }} animate={{
                scale: 1,
                opacity: 0.9
              }} transition={{
                duration: 1,
                repeat: Infinity,
                repeatType: 'reverse'
              }} data-id="element-84" />

                {/* Zen dots */}
                <motion.g animate={{
                rotate: 360
              }} transition={{
                duration: 10,
                repeat: Infinity,
                ease: 'linear'
              }} style={{
                transformOrigin: '100px 100px'
              }} data-id="element-85">
                  <circle cx="100" cy="40" r="6" fill="white" opacity="0.8" data-id="element-86" />
                  <circle cx="140" cy="70" r="6" fill="white" opacity="0.8" data-id="element-87" />
                  <circle cx="60" cy="70" r="6" fill="white" opacity="0.8" data-id="element-88" />
                </motion.g>
              </svg>
            </div>

            <h1 className="text-5xl font-fredoka font-bold text-white mb-4 drop-shadow-lg" data-id="element-89">
              QuestZen AI
            </h1>
            <p className="text-xl text-white/90 font-quicksand mb-8" data-id="element-90">
              Turn goals into focused quests
            </p>

            {/* Feature Pills */}
            <div className="flex flex-wrap gap-3 justify-center" data-id="element-91">
              {['🎯 Smart Quests', '🧘 Focus Mode', '🤖 AI Insights', '👥 Collaborate'].map((feature, i) => <motion.div key={feature} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.8 + i * 0.1
            }} className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-white font-quicksand font-bold text-sm" data-id="element-92">
                  {feature}
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12" data-id="element-93">
        <motion.div initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.5
      }} className="w-full max-w-md" data-id="element-94">
          <div className="text-center mb-8 lg:hidden" data-id="element-95">
            <div className="w-24 h-24 mx-auto mb-4" data-id="element-96">
              <svg viewBox="0 0 200 200" className="w-full h-full" data-id="element-97">
                <defs data-id="element-98">
                  <linearGradient id="mobileGradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-99">
                    <stop offset="0%" stopColor="#8B5CF6" data-id="element-100" />
                    <stop offset="50%" stopColor="#06B6D4" data-id="element-101" />
                    <stop offset="100%" stopColor="#10B981" data-id="element-102" />
                  </linearGradient>
                </defs>
                {/* Zen circle */}
                <circle cx="100" cy="100" r="85" fill="none" stroke="url(#mobileGradient)" strokeWidth="8" opacity="0.3" data-id="element-103" />
                {/* Incomplete circle (Zen symbol) */}
                <path d="M 100 15 A 85 85 0 1 1 99.9 15" fill="none" stroke="url(#mobileGradient)" strokeWidth="10" strokeLinecap="round" opacity="0.9" data-id="element-104" />
                {/* Quest arrow/mountain */}
                <path d="M 100 60 L 70 120 L 100 105 L 130 120 Z" fill="url(#mobileGradient)" opacity="0.9" data-id="element-105" />
              </svg>
            </div>
            <h1 className="text-2xl font-fredoka font-bold bg-gradient-to-r from-game-purple via-game-teal to-game-coral bg-clip-text text-transparent mb-1" data-id="element-106">
              QuestZen AI
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-107">
              Turn goals into focused quests
            </p>
          </div>

          <div className="text-center mb-8" data-id="element-108">
            <h2 className="text-3xl font-fredoka font-bold text-game-dark dark:text-white mb-2" data-id="element-109">
              {title}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-110">
              {subtitle}
            </p>
          </div>

          {children}
        </motion.div>
      </div>
    </div>;
}